import React from 'react';
import { Box, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { IconType } from 'react-icons';

interface CollapsibleCardDetailItemProps {
	icon?: IconType;
	label: string;
	value: string;
	indentWithoutIcon?: boolean;
	variant?: 'plain' | 'inset';
}

/**
 * Reusable component for displaying a single detail row inside CollapsibleCard
 * Shows an icon, label, and value in a consistent layout
 */
const CollapsibleCardDetailItem: React.FC<CollapsibleCardDetailItemProps> = ({
	icon: DetailIcon,
	label,
	value,
	indentWithoutIcon = false,
	variant = 'plain',
}) => {
	if (variant === 'inset') {
		return (
			<HStack gap={3} alignItems="center" bg="surface.subtle" borderRadius="2xl" px={4} py={3}>
				{DetailIcon && <Icon as={DetailIcon} boxSize={5} color="fg.muted" flexShrink={0} />}
				<VStack align="stretch" gap={0} minW={0}>
					<Text fontSize="sm" color="fg.muted" lineHeight="1.25">
						{label}
					</Text>
					<Text textStyle="label.lg" color="brand.fg" lineHeight="1.3">
						{value}
					</Text>
				</VStack>
			</HStack>
		);
	}

	// Centred against the pair, as the inset variant is: nudged to the first line instead, the icon
	// sat against the label and left the value looking unattached to it.
	return (
		<HStack gap={2} alignItems="center">
			{DetailIcon && <Icon as={DetailIcon} boxSize={4} color="brand.fg" flexShrink={0} />}
			{!DetailIcon && indentWithoutIcon && <Box w={5} flexShrink={0} />}
			<VStack align="stretch" gap={0}>
				<Text fontSize="sm" color="fg.muted">
					{label}
				</Text>
				<Text textStyle="label.lg" color="brand.fg">
					{value}
				</Text>
			</VStack>
		</HStack>
	);
};

export default CollapsibleCardDetailItem;
